import logger from '../es6/logger'
import { localStore } from '../es6/localStore'

export async function loadTranslation() {
    const qsParams = new URLSearchParams(window.location.search)
    const locale = qsParams.get('locale') || 'en-US'
    const resources = await import(`../../../translations/marketing_page.${locale}.json`)

    window.EVERSIGN_TRANSLATIONS = {
        ...window.EVERSIGN_TRANSLATIONS,
        ...resources.default,
    }
}

const lang = {
    en: 'en-US',
    de: 'de-DE',
    da: 'da-DK',
    nl: 'nl-NL',
    fr: 'fr-FR',
    hi: 'hi-IN',
    it: 'it-IT',
    pl: 'pl-PL',
    pt: 'pt-PT',
    ru: 'ru-RU',
    tr: 'tr-TR',
    es: 'es-ES',
    se: 'sv-SE',
}

export function getSelectedLanguage(language) {
    const qsParams = new URLSearchParams(window.location.search)

    const locale =
        qsParams.get('locale') || // try to get the selected language via query params
        lang[language] || // some language are lazy defined and need to be looked up
        language || // use the parameter or default language
        'en-US'

    return locale
}

const DEFAULT_RELEASE = 'latest'

function saveLocalTranslations(page, translations, language) {
    logger.log('saveLocalTranslations', page)
    const latestVersion = localStore.getItem('translations.latestVersion')

    if (!latestVersion) {
        localStore.setItem(
            'translations.latestVersion',
            window?.EVERSIGN_CONFIG?.release ?? DEFAULT_RELEASE,
        )
    }

    //
    // Remove old translation keys
    //
    if (latestVersion !== window?.EVERSIGN_CONFIG?.release && localStore.isSupported()) {
        // if localStorage is not accessible removing old translations is not necessary
        for (const key in localStore.getStorage()) {
            if (key.includes(`translations.`) && !key.includes('translations.latestVersion')) {
                logger.log(`Delete old  translation key:`, key)
                localStore.removeItem(key)
            }
        }
        localStore.setItem(
            'translations.latestVersion',
            window?.EVERSIGN_CONFIG?.release ?? DEFAULT_RELEASE,
        )
    }

    localStore.setItem(
        `translations.${window?.EVERSIGN_CONFIG?.release ?? DEFAULT_RELEASE}.${page}.${language}`,
        JSON.stringify(translations),
    )
    if (!window.EVERSIGN_TRANSLATIONS) {
        window.EVERSIGN_TRANSLATIONS = {
            [page]: translations,
        }
    } else {
        window.EVERSIGN_TRANSLATIONS = {
            ...window.EVERSIGN_TRANSLATIONS,
            [page]: translations,
        }
    }
}

function hasTranslationsForPage(page, language) {
    if (!page) {
        return false
    }

    if (!language) {
        return false
    }

    if (!window.EVERSIGN_TRANSLATIONS) {
        return false
    }

    if (window.EVERSIGN_TRANSLATIONS[page]) {
        return true
    } else if (
        localStore.getItem(`translations.${window.EVERSIGN_CONFIG.release}.${page}.${language}`)
    ) {
        return true
    }
    return false
}

function getLocalTranslations(translationKey, page, language) {
    const release = window?.EVERSIGN_CONFIG?.release ?? "latest"
    if (
        window.EVERSIGN_TRANSLATIONS &&
        window.EVERSIGN_TRANSLATIONS[page] &&
        window.EVERSIGN_TRANSLATIONS[page][translationKey]
    ) {
        return window.EVERSIGN_TRANSLATIONS[page][translationKey]
    }
    // for checkout translation ui.confirmation_popup.page_change.text_1
    if (window.EVERSIGN_TRANSLATIONS[translationKey]) {
        return window.EVERSIGN_TRANSLATIONS[translationKey]
    }

    let savedTranslationsString = localStore.getItem(
        `translations.${release}.${page}.${language}`,
    )

    // could not found translations for the current release
    if (!savedTranslationsString) {
        // lookup old release translations
        const latestVersion = localStore.getItem('translations.latestVersion')

        if (!latestVersion) {
            throw new Error('could not determin latest translation version')
        }

        savedTranslationsString = localStore.getItem(
            `translations.${latestVersion}.${page}.${language}`,
        )

        if (!savedTranslationsString) {
            throw new Error(
                `Could not found translations for the release ${release}`,
            )
        }
    }

    const savedTranslations = JSON.parse(savedTranslationsString)
    window.EVERSIGN_TRANSLATIONS[page] = savedTranslations

    return window.EVERSIGN_TRANSLATIONS[page][translationKey]
}

// remove old released translations
// handle localdev

/**
 * loading language file for provided page
 * The default language is en-US
 *
 *
 * @export
 * @param {string} page - pages are defined in translation.js or can be looked up in phase app
 * @param {string} [language=getSelectedLanguage()] - default is en-US
 */
export async function loadTranslationPage(page, language = getSelectedLanguage()) {
    logger.trace(`LOAD translation ns ${page} on Page:`)

    if (hasTranslationsForPage(page, language)) {
        return
    }

    try {
        const resources = await import(`../../../translations/${page}/${page}.${language}.json`)
        saveLocalTranslations(page, resources.default, language)
    } catch (error) {
        logger.error('Could not load translations: ', error)

        throw error
    }
}

/**
 *
 *
 * @export
 * @param {string} translationKey
 * @param {string} page
 * @param {string} [language=getSelectedLanguage()]
 * @return {string}
 */
export function trans(translationKey, page, language = getSelectedLanguage()) {
    if (window.EVERSIGN_TRANSLATIONS) {
        try {
            let translation = getLocalTranslations(translationKey, page, language)

            if (translation && translation.message) {
                return translation.message
            }
        } catch (error) {
            logger.error(error)
            if (!page) {
                throw new Error(`Page was not defined for translation key: ${translationKey} page:${page}`)
            }
            loadTranslationPage(page, language)
        }

        return translationKey
    }
    return translationKey
}
